/**
 * Expenditure path body of `BankTransactionClassificationPanel`. Four
 * sub-categories — Personal/Dismiss collapsed into the panel's
 * top-level TypeToggle action to avoid dual entry points.
 *
 * Sub-category branches:
 *   - ProcessingFee      → server resolves processor via BSW-005 fuzzy match.
 *                          No explicit processor picker in v1 (deferred — see
 *                          Bsw009-implementation-notes.html).
 *   - Refund             → picker for the original Contribution to refund.
 *   - BankFee            → server uses BankAccount.DisplayName as payee.
 *   - GeneralExpenditure → the catch-all: real payee + category + purpose.
 *                          InlineContactForm available for new payees.
 *                          Itemization is a computed verdict (EIR), never a
 *                          classify-time choice — the row always carries its
 *                          real vendor so the resolver can aggregate per payee.
 */
import { useMemo } from 'react';
import { useRefresh, type CurrentRead } from '@signalvote/data';
import { Plus, Receipt, X } from 'lucide-react';
import {
  ContactType,
  ExpenditureCategory,
  ExpenditureClassificationSubCategory,
} from '@signalvote/shared';
import type {
  CapturedReceiptSuggestionDto,
  ComplianceProfileDto,
  ContributionDto,
  CreateFinanceContactRequest,
  FinanceContactDto,
} from '@signalvote/shared';
import {
  ContactCombobox,
  SelectedContactAddressEditor,
} from '@/components/finance/shared/classification';
import { InlineContactForm } from '@/components/finance/entry/entryParts';
import type { ContactAddressPatch } from '@/components/finance/shared/classification';
import { SettlesDebtPicker } from '@/components/finance/debts';
import { RepaysLoanPicker } from '@/components/finance/loans';
import { useProcessorFeeCheck } from '@/hooks/queries/useBankTransactionClassification';
import { formatAmount, formatDate, formatMonthDay } from '@/components/finance/utils';
import { cn } from '@/lib/utils';
import { generalExpenditureCategoryOptions } from './expenditureCategoryOptions';
import type {
  ExpenditureFieldsFromHint,
  UseBankTransactionClassificationFormResult,
} from './useBankTransactionClassificationForm';

// Refund picker only surfaces rows the backend will accept — Confirmed or
// Reported. The server re-checks (see
// BankTransactionClassifyAsExpenditureService.ClassifyAsRefundAsync — RFR-002
// converged this onto a ContributionRefund) so this is a preview, not the
// authoritative guard.
const REFUND_ELIGIBLE_LEDGER_STATUSES: ReadonlySet<string> = new Set([
  'Confirmed',
  'Reported',
]);

interface ExpenditurePathBodyProps {
  form: UseBankTransactionClassificationFormResult;
  payees: FinanceContactDto[];
  refundCandidates: ContributionDto[];
  /** Why the contributions to refund could not load, named instead of an empty picker. */
  refundCandidatesError?: string | null;
  profile: ComplianceProfileDto | null | undefined;
  rowAmount: number;
  rowDescription: string;
  bankTransactionId: string;
  onExcludeWithNote: (note: string) => Promise<void>;
  isExcluding: boolean;
  isCreatingContact: boolean;
  onQuickCreateSave: (request: CreateFinanceContactRequest) => Promise<void>;
  isSavingContactAddress: boolean;
  onSaveContactAddress: (
    contactId: string,
    concurrencyVersion: number,
    patch: ContactAddressPatch,
  ) => void | Promise<void>;
  /**
   * CAP-006 — Unattached pocket receipts that match this bank debit (exact
   * amount + date ±7 days). Empty = no match; the nudge renders nothing.
   */
  receiptSuggestions: CurrentRead<CapturedReceiptSuggestionDto[]>;
  /** The pocket receipt the treasurer opted to attach, or null. */
  selectedReceiptId: string | null;
  /** Toggle a suggestion on (id) or off (null). */
  onSelectReceipt: (id: string | null) => void;
}

// Ordered most-likely-first: a real vendor payment is the common case, so it
// leads. Processing/bank fees and refunds are the long tail.
const SUB_CATEGORY_OPTIONS: ReadonlyArray<{
  value: ExpenditureClassificationSubCategory;
  label: string;
}> = [
  {
    value: ExpenditureClassificationSubCategory.GeneralExpenditure,
    label: 'Vendor / general expenditure',
  },
  { value: ExpenditureClassificationSubCategory.ProcessingFee, label: 'Processing fee' },
  { value: ExpenditureClassificationSubCategory.BankFee, label: 'Bank fee' },
  { value: ExpenditureClassificationSubCategory.Refund, label: 'Refund' },
];

export function ExpenditurePathBody({
  form,
  payees,
  refundCandidates,
  refundCandidatesError = null,
  profile,
  rowAmount,
  rowDescription,
  bankTransactionId,
  onExcludeWithNote,
  isExcluding,
  isCreatingContact,
  onQuickCreateSave,
  isSavingContactAddress,
  onSaveContactAddress,
  receiptSuggestions,
  selectedReceiptId,
  onSelectReceipt,
}: ExpenditurePathBodyProps) {
  const {
    expenditureSubCategory: subCategory,
    setExpenditureSubCategory: setSubCategory,
    expenditureContactId: payeeId,
    setExpenditureContactId: setPayeeId,
    expenditureCategory: category,
    setExpenditureCategory: setCategory,
    expenditurePurpose: purpose,
    setExpenditurePurpose: setPurpose,
    expenditureSettlesDebtId: settlesDebtId,
    setExpenditureSettlesDebtId: setSettlesDebtId,
    expenditureRepaysLoanId: repaysLoanId,
    setExpenditureRepaysLoanId: setRepaysLoanId,
    expenditureRefundOfContributionId: refundOfContributionId,
    setExpenditureRefundOfContributionId: setRefundOfContributionId,
    expenditureShowQuickCreate: showQuickCreate,
    openExpenditureQuickCreate: openQuickCreate,
    cancelExpenditureQuickCreate: cancelQuickCreate,
    expenditureHint: hint,
    expenditureFieldsFromHint: fromHint,
  } = form;

  // At/above the itemization threshold the selected payee's address matters for
  // filing. General expenditures only — Bank fee + Processing fee payees are
  // auto-resolved server-side.
  const itemizationThreshold = profile?.itemizationThreshold ?? 0;
  const overItemizationThreshold = Math.abs(rowAmount) >= itemizationThreshold;

  // General-expenditure payee picker — filter to Payee-type contacts only
  // (per PPR-011 review: collapsing donor and payee names into one picker
  // pollutes donor aggregates).
  const payeeOptions = useMemo(
    () => payees.filter((c) => c.contactType === ContactType.Payee),
    [payees],
  );
  const selectedPayee = useMemo(
    () => payeeOptions.find((c) => c.id === payeeId),
    [payeeOptions, payeeId],
  );

  // Jurisdiction-aware category labels: Alabama relabels Transfer / Reimbursement
  // (both file as ExPurpose "Other") so the picker matches what actually files.
  const categoryOptions = useMemo(
    () => generalExpenditureCategoryOptions(profile?.stateCode),
    [profile?.stateCode],
  );

  // Refund picker — filter to confirmed-or-reported rows (the backend
  // rejects Pending / Voided as OriginalContributionNotConfirmed).
  const refundEligibleCandidates = useMemo(
    () => refundCandidates.filter(
      (c) => REFUND_ELIGIBLE_LEDGER_STATUSES.has(c.ledgerStatus),
    ),
    [refundCandidates],
  );

  // A cautioned hint's note is amber and states the caution; a plain one names its source.
  const hintNote = !hint
    ? null
    : hint.caution
    ? {
        className: 'bg-amber-50 text-amber-800',
        dotClassName: 'bg-amber-500',
        text: `${hint.cautionReason}. Payee and category suggested; purpose is up to you.`,
      }
    : {
        className: 'bg-muted text-foreground',
        dotClassName: 'bg-primary',
        text: `Pre-filled from your ${formatMonthDay(hint.sourceDate)} classification — review and Classify`,
      };

  return (
    <div
      data-testid="expenditure-path-body"
      className="flex-1 space-y-3 overflow-y-auto mb-4"
    >
      {hintNote && (
        <p
          data-testid="classification-hint-note"
          className={cn(
            'flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs',
            hintNote.className,
          )}
        >
          <span
            aria-hidden="true"
            className={cn('h-[7px] w-[7px] shrink-0 rounded-full', hintNote.dotClassName)}
          />
          {hintNote.text}
        </p>
      )}
      <div>
        <label
          htmlFor="bt-classify-exp-subcategory"
          className="block text-sm font-medium mb-1"
        >
          What kind of payment is this? *
          {fromHint.kind && <FromHintLabel field="kind" />}
        </label>
        <select
          id="bt-classify-exp-subcategory"
          value={subCategory ?? ''}
          onChange={(e) =>
            setSubCategory(
              e.target.value === ''
                ? null
                : (e.target.value as ExpenditureClassificationSubCategory),
            )
          }
          className="w-full rounded-md border px-3 py-2 text-sm"
        >
          <option value="">Select one...</option>
          {SUB_CATEGORY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* CAP-006 — pocket-receipt suggest-match. Hidden on the Refund
          sub-category (a refund creates no Expenditure to attach a receipt to;
          the server rejects a receipt there). Renders nothing while loading
          or when there are no matches — no empty-state noise — but names a
          read that failed, so "no match" is never claimed unchecked. */}
      {subCategory !== ExpenditureClassificationSubCategory.Refund
        && receiptSuggestions.status === 'ready' && receiptSuggestions.data.length > 0 && (
        <ReceiptSuggestions
          suggestions={receiptSuggestions.data}
          selectedReceiptId={selectedReceiptId}
          onSelectReceipt={onSelectReceipt}
        />
      )}
      {subCategory !== ExpenditureClassificationSubCategory.Refund
        && (receiptSuggestions.status === 'failed' || receiptSuggestions.status === 'offline') && (
        <p
          data-testid="receipt-suggestions-unready"
          className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground"
        >
          {receiptSuggestions.status === 'offline'
            ? "You're offline, so we couldn't check your receipts for a match to this line."
            : "Couldn't check your receipts for a match to this line."}
        </p>
      )}

      {subCategory === ExpenditureClassificationSubCategory.ProcessingFee && (
        <ProcessingFeeBody
          description={rowDescription}
          bankTransactionId={bankTransactionId}
          onExcludeWithNote={onExcludeWithNote}
          isExcluding={isExcluding}
        />
      )}

      {subCategory === ExpenditureClassificationSubCategory.BankFee && (
        <p
          data-testid="bank-fee-body"
          className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground"
        >
          Will book this row as a bank service charge. The payee defaults to
          the account's institution name; you can edit the expenditure later
          if you need a more specific payee.
        </p>
      )}

      {subCategory === ExpenditureClassificationSubCategory.Refund && (
        <div>
          <label
            htmlFor="bt-classify-exp-refund-original"
            className="block text-sm font-medium mb-1"
          >
            Original contribution *
          </label>
          <select
            id="bt-classify-exp-refund-original"
            value={refundOfContributionId}
            onChange={(e) => setRefundOfContributionId(e.target.value)}
            className="w-full rounded-md border px-3 py-2 text-sm"
          >
            <option value="">Select a contribution to refund...</option>
            {refundEligibleCandidates.map((c) => (
              <option key={c.id} value={c.id}>
                {c.contactName} — ${c.amount.toFixed(2)} ({c.date})
              </option>
            ))}
          </select>
          {refundCandidatesError && (
            <p role="alert" className="mt-1 text-xs text-destructive">
              {refundCandidatesError}
            </p>
          )}
          <p className="mt-1 text-xs text-muted-foreground">
            Refund amount must not exceed the original contribution amount.
          </p>
        </div>
      )}

      {subCategory === ExpenditureClassificationSubCategory.GeneralExpenditure && (
        <>
          <div>
            <label
              htmlFor="bt-classify-exp-payee"
              className="block text-sm font-medium mb-1"
            >
              Payee *
              {fromHint.payee && <FromHintLabel field="payee" />}
            </label>
            <ContactCombobox
              id="bt-classify-exp-payee"
              value={payeeId}
              onChange={setPayeeId}
              contacts={payeeOptions}
              placeholder="Select a payee..."
              searchPlaceholder="Search payees..."
              emptyText="No payees found."
            />
            {!showQuickCreate && (
              <button
                type="button"
                onClick={openQuickCreate}
                className="mt-1 flex items-center gap-1 text-xs text-primary underline"
              >
                <Plus className="h-3 w-3" />
                Create new payee
              </button>
            )}
            {showQuickCreate && (
              <div className="mt-2">
                {/* Address is always offered at payee create time: a payee can cross the
                    itemization threshold later (per-payee cycle aggregate), so capturing it once
                    here avoids a name-only contact fixed up at filing. It never gates Save. */}
                <InlineContactForm
                  contactType={ContactType.Payee}
                  showEmployerOccupation={false}
                  supportedOrganizationTypes={profile?.supportedOrganizationTypes ?? []}
                  addressAutocomplete
                  isSaving={isCreatingContact}
                  onSave={onQuickCreateSave}
                  onCancel={cancelQuickCreate}
                />
              </div>
            )}
            {!showQuickCreate && selectedPayee && (
              <SelectedContactAddressEditor
                key={selectedPayee.id}
                contact={selectedPayee}
                noun="payee"
                addressMattersForFiling={overItemizationThreshold}
                isSaving={isSavingContactAddress}
                onSaveAddress={(patch) =>
                  onSaveContactAddress(
                    selectedPayee.id,
                    selectedPayee.concurrencyVersion,
                    patch,
                  )
                }
              />
            )}
          </div>

          <div>
            <label
              htmlFor="bt-classify-exp-category"
              className="block text-sm font-medium mb-1"
            >
              Category *
              {fromHint.category && <FromHintLabel field="category" />}
            </label>
            <select
              id="bt-classify-exp-category"
              value={category}
              onChange={(e) => setCategory(e.target.value as ExpenditureCategory)}
              className="w-full rounded-md border px-3 py-2 text-sm"
            >
              {categoryOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="bt-classify-exp-purpose"
              className="block text-sm font-medium mb-1"
            >
              Purpose *
              {fromHint.purpose && <FromHintLabel field="purpose" />}
            </label>
            <input
              id="bt-classify-exp-purpose"
              type="text"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              className="w-full rounded-md border px-3 py-2 text-sm"
              placeholder="e.g., Yard signs"
            />
          </div>

          {/* ADR-031 — tag this money-out as settling a debt (renders nothing when there
              are no debts to point at). */}
          <SettlesDebtPicker
            value={settlesDebtId}
            onChange={(next) => {
              setSettlesDebtId(next);
              if (next !== null) setRepaysLoanId(null);
            }}
            id="bt-classify-exp-settles-debt"
          />

          {/* Tag this money-out as repaying a loan (renders nothing when there are no loans to
              point at). This is where a Louisiana repayment becomes filable: it reports as a
              Schedule B payment beneath its loan, and an unlinked one blocks filing rather than
              disclosing in the wrong place. Mutually exclusive with the debt link above — one
              payment settles one obligation. */}
          <RepaysLoanPicker
            value={repaysLoanId}
            onChange={(next) => {
              setRepaysLoanId(next);
              if (next !== null) setSettlesDebtId(null);
            }}
            id="bt-classify-exp-repays-loan"
          />
        </>
      )}
    </div>
  );
}

/** Marks a classify-form field that still holds the classification hint's value. */
function FromHintLabel({ field }: { field: keyof ExpenditureFieldsFromHint }) {
  return (
    <span data-testid={`from-hint-${field}`} className="ml-1 text-[11px] font-normal text-slate-400">
      · from hint
    </span>
  );
}

interface ProcessingFeeBodyProps {
  description: string;
  bankTransactionId: string;
  /** Called when the one-gesture "exclude" button is clicked. Receives the prefilled note. */
  onExcludeWithNote: (note: string) => Promise<void>;
  isExcluding: boolean;
}

// Bank-fee-line steering (spec §D7): some processors debit their fee as a
// standalone bank line. When that line imports after the fee was already booked,
// classifying it as "Processing fee" would create a duplicate. When a fee for the
// resolved processor is already booked this period, offer the one-gesture
// exclude-from-report resolution (ADR-014) with a prefilled note instead.
function ProcessingFeeBody({
  description,
  bankTransactionId,
  onExcludeWithNote,
  isExcluding,
}: ProcessingFeeBodyProps) {
  const feeCheckRead = useProcessorFeeCheck(bankTransactionId);
  const refresh = useRefresh();

  if (feeCheckRead.status === 'failed' || feeCheckRead.status === 'offline') {
    return (
      <div
        data-testid="processing-fee-body"
        className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm space-y-2"
      >
        <p className="text-amber-900">
          {feeCheckRead.status === 'offline' ? "You're offline. " : ''}
          Couldn't check whether this processor's fee is already booked this period, so
          classifying it may book the fee twice.
        </p>
        <button
          type="button"
          onClick={() => void refresh(useProcessorFeeCheck, bankTransactionId)}
          className="inline-flex items-center rounded-md bg-amber-100 px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-200"
        >
          Check again
        </button>
      </div>
    );
  }

  if (feeCheckRead.status !== 'ready') {
    return (
      <p
        data-testid="processing-fee-body"
        className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground"
      >
        Checking processor…
      </p>
    );
  }

  const feeCheck = feeCheckRead.data;
  if (feeCheck.feeAlreadyBooked && feeCheck.processorName != null) {
    const bookedDateLabel = feeCheck.bookedFeeDate
      ? new Date(feeCheck.bookedFeeDate + 'T00:00:00').toLocaleDateString('en-US', {
          month: 'numeric',
          day: 'numeric',
        })
      : 'this period';
    const prefillNote = `matches the fee booked ${bookedDateLabel}`;
    const bookedAmountLabel = feeCheck.bookedFeeAmount != null
      ? `$${feeCheck.bookedFeeAmount.toFixed(2)}`
      : 'a';

    return (
      <div
        data-testid="processing-fee-body"
        className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm space-y-2"
      >
        <p className="text-amber-900">
          A {bookedAmountLabel} {feeCheck.processorName} fee is already booked for this
          period. If this bank line is that fee →
        </p>
        <button
          type="button"
          disabled={isExcluding}
          onClick={() => void onExcludeWithNote(prefillNote)}
          className="inline-flex items-center rounded-md bg-amber-100 px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-200 disabled:opacity-50"
          data-testid="processing-fee-exclude-button"
        >
          Mark this line as excluded from the report
        </button>
      </div>
    );
  }

  // No booked fee — normal classify path.
  return (
    <p
      data-testid="processing-fee-body"
      className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground"
    >
      Will book this row to the matching payment processor based on the bank
      description (<code className="text-xs">{description}</code>). If no
      processor matches, the server will surface an error and you can pick
      "Vendor / general expenditure" instead.
    </p>
  );
}

interface ReceiptSuggestionsProps {
  suggestions: CapturedReceiptSuggestionDto[];
  selectedReceiptId: string | null;
  onSelectReceipt: (id: string | null) => void;
}

// CAP-006 — a compact, low-weight nudge listing the Unattached pocket receipts
// that match this bank debit (exact amount + date ±7 days). The treasurer opts
// one in — nothing is auto-attached (finance product direction: suggest, don't
// auto-apply). The chosen receipt attaches to the Expenditure in the same
// commit as the classify. Only rendered when there's at least one match, so the
// container itself is the "has matches" signal (no empty state).
function ReceiptSuggestions({
  suggestions,
  selectedReceiptId,
  onSelectReceipt,
}: ReceiptSuggestionsProps) {
  const isSingle = suggestions.length === 1;
  return (
    <div
      data-testid="receipt-suggestions"
      className="rounded-md border border-border bg-muted/30 p-3 text-sm"
    >
      <div className="mb-2 flex items-center gap-1.5 text-muted-foreground">
        <Receipt className="h-3.5 w-3.5" />
        <span className="font-medium text-foreground">
          {isSingle
            ? 'A captured receipt matches this transaction'
            : `${suggestions.length} captured receipts match this transaction`}
        </span>
      </div>
      <ul className="space-y-1.5">
        {suggestions.map((r) => {
          const isSelected = r.id === selectedReceiptId;
          const meta = [
            r.amount != null ? formatAmount(r.amount) : null,
            r.date ? formatDate(r.date) : null,
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <li
              key={r.id}
              data-testid={`receipt-suggestion-${r.id}`}
              className={
                'flex items-center justify-between gap-2 rounded border px-2 py-1.5 '
                + (isSelected
                  ? 'border-primary bg-primary/5'
                  : 'border-transparent bg-background')
              }
            >
              <div className="min-w-0">
                <div className="truncate font-medium text-foreground">
                  {r.vendor?.trim() || r.fileName}
                </div>
                {(meta || r.vendor) && (
                  <div className="truncate text-xs text-muted-foreground">
                    {[meta, r.vendor ? r.fileName : null].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
              {isSelected ? (
                <button
                  type="button"
                  data-testid={`receipt-suggestion-detach-${r.id}`}
                  onClick={() => onSelectReceipt(null)}
                  className="inline-flex shrink-0 items-center gap-1 rounded px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10"
                >
                  Attached
                  <X className="h-3 w-3" />
                </button>
              ) : (
                <button
                  type="button"
                  data-testid={`receipt-suggestion-attach-${r.id}`}
                  onClick={() => onSelectReceipt(r.id)}
                  className="inline-flex shrink-0 items-center rounded border border-border bg-background px-2 py-1 text-xs font-medium text-foreground hover:bg-accent"
                >
                  Attach
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

